import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";

// @mui material components
import { Grid, Box, IconButton, Drawer } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import FTextField from "components/Form/FTextField";
import ConfigDropdown from "components/Dropdown/ConfigDropdown";
import ImageUpload from "components/ImageUpload/imageUpload";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";

// Icons and Constants
import Constants, {
  Common,
  BackendFrontend,
  Icons,
  Colors,
  ButtonTitles,
  defaultData,
} from "utils/Constants";
import pattern from "utils/Patterns";

import MDTypography from "components/MDTypography";

// Redux
import {
  updateProfileContractualThunk,
  createProfileContractualThunk,
} from "redux/Thunks/SuperAdmin";
import { useDispatch } from "react-redux";
import { openSnackbar } from "redux/Slice/Notification";

// Personal COnfig
import { contractualDetailsFields } from "examples/Drawers/profile/PersonalConfig";
import { useParams } from "react-router-dom";
import TaxRegistrationField from "examples/Drawers/profile/TaxRegistrationField";
import { mapTypeToDB, mapTypeFromDB } from "examples/Drawers/profile/models/TaxRegistration";

const mapOptionsForConfigDropdown = (options = []) =>
  options.map((option) => ({
    ...option,
    [Constants.MONGOOSE_ID]: option[Constants.MONGOOSE_ID] || option.id,
  }));

function ContractualDetailsDrawer({ open, onClose, data = {}, contractualDetailId, userId }) {
  // Format initial data to separate primary and Secondpassports
  const formatInitialData = (initialData) => {
    // Extract primary and secondary passport data
    const primaryPassportData = initialData?.identityProof?.filter((doc) => doc.isPrimary) || [];
    const secondaryPassportData =
      initialData?.identityProof?.filter((doc) => doc.isSecondary) || [];

    // Format driving license data
    const drivingLicenseData = initialData.drivingLicence?.[0] || {};

    // Liability insurance data
    const liabilityInsuranceData = initialData?.liabilityInsurance?.[0] || {};

    // Health insurance data
    const healthInsuranceData = initialData?.healthInsurance?.[0] || {};

    // Find the matching employment type option
    const mapEmploymentTypeFromAPI = (apiValue) => {
      if (!apiValue) return "";

      if (apiValue === Common.EXTERNAL_EMPLOYEE_API) {
        return Common.EXTERNAL_EMPLOYEE;
      }
      if (apiValue === Common.INTERNAL_EMPLOYEE_API) {
        return Common.INTERNAL_EMPLOYEE;
      }

      const employmentTypeOptions =
        contractualDetailsFields.find((field) => field.id === "employmentType")?.options || [];
      const matchingOption = employmentTypeOptions.find((opt) => opt.id === apiValue);

      return matchingOption ? apiValue : "";
    };

    // Format seamans book data
    const formattedSeamansBook =
      initialData.seamansBook?.map((book) => ({
        name: book.name || "",
        url: book.url || "",
        size: book.size || 0,
        fromDate: book.fromDate
          ? moment(book.fromDate).format(defaultData.DATABSE_DATE_FORMAT)
          : "",
        toDate: book.toDate ? moment(book.toDate).format(defaultData.DATABSE_DATE_FORMAT) : "",
        status: book.status || defaultData.PENDING_STATUS,
        comment: book.comment || "",
      })) || [];

    // Format driving license document data from drivingLicence array
    const formattedDrivingLicenseDoc =
      drivingLicenseData?.name || drivingLicenseData?.url
        ? [
            {
              name: drivingLicenseData.name || "",
              url: drivingLicenseData.url || "",
              size: drivingLicenseData.size || 0,
            },
          ]
        : [];

    // Format company registration document data from companyRegistration array
    const formattedCompanyRegistrationDoc =
      initialData.companyRegistrationDocument?.map((doc) => ({
        name: doc.name || "",
        url: doc.url || "",
        size: doc.size || 0,
      })) || [];

    // Format bank account document data from bankAccount array
    const formattedBankAccountDoc =
      initialData.bankAccountDocument?.map((doc) => ({
        name: doc.name || "",
        url: doc.url || "",
        size: doc.size || 0,
      })) || [];
    return {
      ...initialData,
      birthDate: initialData.birthDate,
      primaryPassport: initialData.passport || "",
      primaryPassportIssueDate: primaryPassportData[0]?.fromDate
        ? moment(primaryPassportData[0].fromDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      primaryPassportExpiryDate: primaryPassportData[0]?.toDate
        ? moment(primaryPassportData[0].toDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      primaryPassportDoc: primaryPassportData.map((doc) => ({
        ...doc,
        name: doc.name,
        url: doc.url,
        size: doc.size,
        fromDate: doc.fromDate ? moment(doc.fromDate).format(defaultData.DATABSE_DATE_FORMAT) : "",
        toDate: doc.toDate ? moment(doc.toDate).format(defaultData.DATABSE_DATE_FORMAT) : "",
        status: doc.status,
        comment: doc.comment,
        isPrimary: true,
        isSecondary: false,
      })),
      secondaryPassport: initialData.secondaryPassport || "",
      secondaryPassportIssueDate: secondaryPassportData[0]?.fromDate
        ? moment(secondaryPassportData[0].fromDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      secondaryPassportExpiryDate: secondaryPassportData[0]?.toDate
        ? moment(secondaryPassportData[0].toDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      secondaryPassportDoc: secondaryPassportData.map((doc) => ({
        ...doc,
        name: doc.name,
        url: doc.url,
        size: doc.size,
        fromDate: doc.fromDate ? moment(doc.fromDate).format(defaultData.DATABSE_DATE_FORMAT) : "",
        toDate: doc.toDate ? moment(doc.toDate).format(defaultData.DATABSE_DATE_FORMAT) : "",
        status: doc.status,
        comment: doc.comment,
        isPrimary: false,
        isSecondary: true,
      })),
      seamansBook: formattedSeamansBook,
      seamansBookIssueDate: initialData.seamansBook?.[0]?.fromDate
        ? moment(initialData.seamansBook[0].fromDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      seamansBookExpiryDate: initialData.seamansBook?.[0]?.toDate
        ? moment(initialData.seamansBook[0].toDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      employmentType: mapEmploymentTypeFromAPI(initialData.employmentType),
      drivingLicenseNumber: drivingLicenseData.licenseNumber || "",
      drivingLicenseIssueDate: drivingLicenseData.issueDate
        ? moment(drivingLicenseData.issueDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      drivingLicenseExpiryDate: drivingLicenseData.expiryDate
        ? moment(drivingLicenseData.expiryDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      drivingLicenseDoc: formattedDrivingLicenseDoc,
      liabilityIssueDate: liabilityInsuranceData.fromDate
        ? moment(liabilityInsuranceData.fromDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      liabilityExpiryDate: liabilityInsuranceData.toDate
        ? moment(liabilityInsuranceData.toDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      healthIssueDate: healthInsuranceData.fromDate
        ? moment(healthInsuranceData.fromDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      healthExpiryDate: healthInsuranceData.toDate
        ? moment(healthInsuranceData.toDate).format(defaultData.DATABSE_DATE_FORMAT)
        : "",
      companyRegistrationDoc: formattedCompanyRegistrationDoc,
      bankAccountDoc: formattedBankAccountDoc,
      taxIdNumbers: (initialData.taxIdNumbers || []).map((tax) => ({
        id: crypto.randomUUID(),
        type: mapTypeFromDB(tax.type), // Convert lowercase DB type to uppercase frontend type
        value: tax.number || "", // Map "number" to "value" for frontend
        documents: (tax.documents || []).map((doc) => ({
          name: doc.name || "",
          url: doc.url || "",
          size: doc.size || 0,
        })),
      })),
    };
  };

  const [formData, setFormData] = useState(formatInitialData(data));
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const handleDrawerClose = () => {
    setErrors({});
    setFormData(formatInitialData(data));
    onClose();
  };

  const handleChange = (name, value) => {
    // For employment type, extract the id if it's an object
    let newValue = value;
    if (name === "employmentType" && value && typeof value === "object") {
      newValue = value.id;
    } else if (name === "companyCountry") {
      // For company country, extract title from object or use string value
      newValue =
        typeof value === "object" && value !== null ? value.title || value.country || value : value;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleImageChange = (name, imageValues) => {
    const imageInfoArray = imageValues.map((item) => {
      const fromDate = item.fromDate || "";
      const toDate = item.toDate || "";

      const baseInfo = {
        url: item.url,
        size: item.size,
        name: item.name,
        fromDate,
        toDate,
        status: defaultData.PENDING_STATUS,
        comment: "",
      };

      if (name === "primaryPassportDoc") {
        return {
          ...baseInfo,
          isPrimary: true,
          isSecondary: false,
        };
      }

      if (name === "secondaryPassportDoc") {
        return {
          ...baseInfo,
          isPrimary: false,
          isSecondary: true,
        };
      }

      return baseInfo;
    });

    setFormData((prev) => ({
      ...prev,
      [name]: imageInfoArray,
    }));
  };

  const handleImageCancel = (fieldName, updatedImageUrl) => {
    const newImageUrlArray = updatedImageUrl || formData[fieldName];
    const filteredImageUrlArray = newImageUrlArray.filter((img) => img.url !== Common.FRAME);

    setFormData((prev) => ({
      ...prev,
      [fieldName]: filteredImageUrlArray,
    }));
  };

  // Helper function to check if a field has a value
  const hasFieldValue = (value) => {
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  // Validate field groups - all fields must be filled or all must be empty
  const validateFieldGroup = (groupFields, groupName) => {
    const fieldValues = groupFields.map((fieldId) => hasFieldValue(formData[fieldId]));
    const filledCount = fieldValues.filter(Boolean).length;
    const groupErrors = {};

    // If some fields are filled but not all, show error on empty fields
    if (filledCount > 0 && filledCount < groupFields.length) {
      groupFields.forEach((fieldId, index) => {
        if (!fieldValues[index]) {
          groupErrors[fieldId] = `Please complete ${groupName} fields`;
        }
      });
    }
    return groupErrors;
  };

  const handleSubmit = async () => {
    // Validate form
    const newErrors = {};

    // Field group validation - all-or-nothing groups
    const fieldGroups = [
      {
        name: "Primary Passport",
        fields: [
          "primaryPassport",
          "primaryPassportDoc",
          "primaryPassportIssueDate",
          "primaryPassportExpiryDate",
        ],
      },
      {
        name: "Secondary Passport",
        fields: [
          "secondaryPassport",
          "secondaryPassportDoc",
          "secondaryPassportIssueDate",
          "secondaryPassportExpiryDate",
        ],
      },
      {
        name: "Driving License",
        fields: [
          "drivingLicenseNumber",
          "drivingLicenseDoc",
          "drivingLicenseIssueDate",
          "drivingLicenseExpiryDate",
        ],
      },
      {
        name: "Seaman's Book",
        fields: ["seamansBook", "seamansBookIssueDate", "seamansBookExpiryDate"],
      },
      {
        name: "Health Insurance",
        fields: ["healthInsurance", "healthInsuranceId", "healthIssueDate", "healthExpiryDate"],
        condition: (values) => values.employmentType === Common.SELF_EMPLOYED,
      },
      {
        name: "Liability Insurance",
        fields: [
          "liabilityInsurance",
          "liabilityInsuranceId",
          "liabilityIssueDate",
          "liabilityExpiryDate",
        ],
        condition: (values) => values.employmentType === Common.SELF_EMPLOYED,
      },
      {
        name: "Bank Account",
        fields: ["bankAccount", "bankAccountDoc"],
        condition: (values) => values.employmentType !== Common.EXTERNAL_EMPLOYEE,
      },
      {
        name: "Company Registration",
        fields: ["companyRegistrationNumber", "companyRegistrationDoc"],
        condition: (values) => values.employmentType !== Common.INTERNAL_EMPLOYEE,
      },
    ];

    // Validate all field groups first
    fieldGroups.forEach((group) => {
      if (group.condition && !group.condition(formData)) {
        return;
      }

      const groupErrors = validateFieldGroup(group.fields, group.name);
      Object.assign(newErrors, groupErrors);
    });

    // If field group validation fails, show errors and return early
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Validate date relationships - expiry dates must be greater than issue dates
    const validateDateRelationship = (issueDate, expiryDate, expiryFieldName, fieldLabel) => {
      if (issueDate && expiryDate) {
        const issue = moment(issueDate);
        const expiry = moment(expiryDate);
        if (expiry.isSameOrBefore(issue)) {
          newErrors[expiryFieldName] = `${fieldLabel} expiry date must be after issue date`;
        }
      }
    };

    // Validate all date pairs
    validateDateRelationship(
      formData.primaryPassportIssueDate,
      formData.primaryPassportExpiryDate,
      "primaryPassportExpiryDate",
      "Primary Passport"
    );
    validateDateRelationship(
      formData.secondaryPassportIssueDate,
      formData.secondaryPassportExpiryDate,
      "secondaryPassportExpiryDate",
      "Secondary Passport"
    );
    validateDateRelationship(
      formData.drivingLicenseIssueDate,
      formData.drivingLicenseExpiryDate,
      "drivingLicenseExpiryDate",
      "Driving License"
    );
    validateDateRelationship(
      formData.seamansBookIssueDate,
      formData.seamansBookExpiryDate,
      "seamansBookExpiryDate",
      "Seaman's Book"
    );
    if (formData.employmentType === Common.SELF_EMPLOYED) {
      validateDateRelationship(
        formData.healthIssueDate,
        formData.healthExpiryDate,
        "healthExpiryDate",
        "Health Insurance"
      );
      validateDateRelationship(
        formData.liabilityIssueDate,
        formData.liabilityExpiryDate,
        "liabilityExpiryDate",
        "Liability Insurance"
      );
    }

    // If date validation fails, show errors and return early
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Individual field validation
    contractualDetailsFields.forEach((field) => {
      if (field.condition && !field.condition(formData)) {
        return;
      }

      // Check if the field is required and empty
      // Special handling for taxIdNumbers (array of objects)
      if (field.id === "taxIdNumbers") {
        if (field.IsRequired && (!formData[field.id] || formData[field.id].length === 0)) {
          newErrors[field.id] = Constants.FIELD_REQUIRED_ERROR;
        } else if (formData[field.id] && formData[field.id].length > 0) {
          // Validate each tax registration entry
          formData[field.id].forEach((reg) => {
            if (!reg.value || !reg.documents || reg.documents.length === 0) {
              if (!reg.value) {
                newErrors[
                  `tax-${reg.id}-value`
                ] = `Please complete all ${reg.type} registration details`;
              }
              if (!reg.documents || reg.documents.length === 0) {
                newErrors[
                  `tax-${reg.id}-documents`
                ] = `Please complete all ${reg.type} registration details`;
              }
            }
          });
        }
      } else if (field.IsRequired && (!formData[field.id] || formData[field.id].length === 0)) {
        newErrors[field.id] = Constants.FIELD_REQUIRED_ERROR;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const {
      primaryPassportDoc,
      primaryPassportExpiryDate,
      primaryPassportIssueDate,
      secondaryPassportDoc,
      secondaryPassportExpiryDate,
      secondaryPassportIssueDate,
      seamansBookIssueDate,
      seamansBookExpiryDate,
      drivingLicenseExpiryDate,
      drivingLicenseIssueDate,
      drivingLicenseDoc,
      liabilityExpiryDate,
      liabilityIssueDate,
      healthExpiryDate,
      healthIssueDate,
      drivingLicenseNumber,
      primaryPassport,
      employmentType,
      companyRegistrationDoc,
      bankAccountDoc,
      taxIdNumbers,
      ...rest
    } = formData;
    // Format data back to API structure
    const formattedData = {
      ...rest,
      ...(employmentType
        ? (() => {
            let type = employmentType;
            if (employmentType === Common.EXTERNAL_EMPLOYEE) {
              type = Common.EXTERNAL_EMPLOYEE_API;
            }
            if (employmentType === Common.INTERNAL_EMPLOYEE) {
              type = Common.INTERNAL_EMPLOYEE_API;
            }
            return { employmentType: type };
          })()
        : {}),
      birthDate: formData.birthDate,
      passport: formData.primaryPassport,
      drivingLicence: formData.drivingLicenseNumber
        ? [
            {
              licenseNumber: formData.drivingLicenseNumber,
              issueDate: formData.drivingLicenseIssueDate
                ? moment(formData.drivingLicenseIssueDate).format(defaultData.DATABSE_DATE_FORMAT)
                : "",
              expiryDate: formData.drivingLicenseExpiryDate
                ? moment(formData.drivingLicenseExpiryDate).format(defaultData.DATABSE_DATE_FORMAT)
                : "",
              name: formData.drivingLicenseDoc?.[0]?.name || "",
              size: formData.drivingLicenseDoc?.[0]?.size || 0,
              url: formData.drivingLicenseDoc?.[0]?.url || "",
            },
          ]
        : [],
      companyRegistrationDocument: companyRegistrationDoc,
      bankAccountDocument: bankAccountDoc,
      identityProof: [
        ...(formData.primaryPassportDoc?.map((doc) => ({
          name: doc.name,
          size: doc.size,
          url: doc.url,
          status: doc.status || defaultData.PENDING_STATUS,
          comment: doc.comment || "",
          fromDate: formData.primaryPassportIssueDate
            ? moment(formData.primaryPassportIssueDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
          toDate: formData.primaryPassportExpiryDate
            ? moment(formData.primaryPassportExpiryDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
          passport: formData.primaryPassport,
          isPrimary: true,
          isSecondary: false,
        })) || []),
        ...(formData.secondaryPassportDoc?.map((doc) => ({
          name: doc.name,
          size: doc.size,
          url: doc.url,
          status: doc.status || defaultData.PENDING_STATUS,
          comment: doc.comment || "",
          fromDate: formData.secondaryPassportIssueDate
            ? moment(formData.secondaryPassportIssueDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
          toDate: formData.secondaryPassportExpiryDate
            ? moment(formData.secondaryPassportExpiryDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
          passport: formData.secondaryPassport,
          isPrimary: false,
          isSecondary: true,
        })) || []),
      ],
      liabilityInsurance:
        formData.liabilityInsurance?.map((doc) => ({
          name: doc.name,
          size: doc.size,
          url: doc.url,
          status: doc.status || defaultData.PENDING_STATUS,
          comment: doc.comment || "",
          fromDate: formData.liabilityIssueDate
            ? moment(formData.liabilityIssueDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
          toDate: formData.liabilityExpiryDate
            ? moment(formData.liabilityExpiryDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
        })) || [],
      healthInsurance:
        formData.healthInsurance?.map((doc) => ({
          name: doc.name,
          size: doc.size,
          url: doc.url,
          status: doc.status || defaultData.PENDING_STATUS,
          comment: doc.comment || "",
          fromDate: formData.healthIssueDate
            ? moment(formData.healthIssueDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
          toDate: formData.healthExpiryDate
            ? moment(formData.healthExpiryDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
        })) || [],
      seamansBook:
        formData.seamansBook?.map((doc) => ({
          name: doc.name,
          size: doc.size,
          url: doc.url,
          status: doc.status || defaultData.PENDING_STATUS,
          comment: doc.comment || "",
          fromDate: formData.seamansBookIssueDate
            ? moment(formData.seamansBookIssueDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
          toDate: formData.seamansBookExpiryDate
            ? moment(formData.seamansBookExpiryDate).format(defaultData.DATABSE_DATE_FORMAT)
            : "",
        })) || [],
      taxIdNumbers:
        taxIdNumbers?.map((tax) => ({
          type: mapTypeToDB(tax.type), // Convert uppercase frontend type to lowercase DB type
          number: tax.value || "", // Map "value" to "number" for backend
          documents: (tax.documents || []).map((doc) => ({
            name: doc.name || "",
            size: doc.size ? String(doc.size) : "",
            url: doc.url || "",
          })),
        })) || [],
      personnelUserId: id || userId,
    };
    const changedFields = {};
    Object.keys(formattedData).forEach((key) => {
      if (JSON.stringify(formattedData[key]) !== JSON.stringify(data[key])) {
        changedFields[key] = formattedData[key];
      }
    });

    const dataBody = {
      body: changedFields,
      id: contractualDetailId,
    };
    const apiToCall = contractualDetailId
      ? updateProfileContractualThunk
      : createProfileContractualThunk;
    setLoading(true);
    const res = await dispatch(apiToCall(dataBody));
    setLoading(false);
    if (res.payload.status === Common.API_STATUS_200) {
      dispatch(
        openSnackbar({
          message: Constants.PROFILE_UPDATED_SUCCESS,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      setErrors({});
      onClose();
    } else if (res.payload.status === Common.API_STATUS_422) {
      const errorData = res.payload.data?.data?.error || [];
      let errorMessage = Constants.SOMETHING_WENT_WRONG;

      if (errorData.length > 0) {
        // Check if the error object contains any keys
        const errorKeys = Object.keys(errorData[0]);
        if (errorKeys.length > 0) {
          // Use the first key's value as the error message
          errorMessage = errorData[0][errorKeys[0]] || Constants.SOMETHING_WENT_WRONG;
        }
      }

      dispatch(
        openSnackbar({
          message: errorMessage,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    } else {
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
    const keysToDelete = [
      "primaryPassport",
      "primaryPassportDoc",
      "primaryPassportIssueDate",
      "primaryPassportExpiryDate",
      "secondaryPassport",
      "secondaryPassportDoc",
      "secondaryPassportIssueDate",
      "secondaryPassportExpiryDate",
      "seamansBookIssueDate",
      "seamansBookExpiryDate",
      "drivingLicenseNumber",
      "drivingLicenseIssueDate",
      "drivingLicenseExpiryDate",
      "drivingLicenseDoc",
      "liabilityIssueDate",
      "liabilityExpiryDate",
      "healthIssueDate",
      "healthExpiryDate",
      "companyRegistrationDoc",
      "bankAccountDoc",
      "taxIdNumbers",
    ];
    keysToDelete.forEach((key) => delete formattedData[key]);
  };

  const formatFieldValueToArray = (value) => {
    if (!value || value === "") return [];
    return Array.isArray(value) ? value : [value];
  };

  const renderField = (field) => {
    // Skip rendering if condition is not met
    if (field.condition && !field.condition(formData)) {
      return null;
    }

    const fieldValue = formData[field.id] || "";

    switch (field.type) {
      case BackendFrontend.OPTIONS:
        if (field.id === "employmentType") {
          const employmentTypeOptions = mapOptionsForConfigDropdown(field.options);
          const hasSelectedOption = employmentTypeOptions.some(
            (opt) => opt[Constants.MONGOOSE_ID] === fieldValue
          );

          return (
            <ConfigDropdown
              label={field.IsRequired ? `${field.label}*` : field.label}
              name={field.id}
              id={field.id}
              menu={employmentTypeOptions}
              value={hasSelectedOption ? fieldValue : ""}
              error={errors[field.id]}
              helperText={errors[field.id]}
              handleChange={(_e, value) => handleChange(field.id, value?.id || value || "")}
            />
          );
        }
        if (field.id === "companyCountry") {
          const selectedOption = field.options.find(
            (item) => item.title === fieldValue || item[Constants.MONGOOSE_ID] === fieldValue
          );
          const fallbackOption =
            fieldValue && !selectedOption
              ? { [Constants.MONGOOSE_ID]: fieldValue, title: fieldValue }
              : null;
          const companyCountryOptions = fallbackOption
            ? [fallbackOption, ...field.options]
            : field.options;

          return (
            <CustomAutoComplete
              label={field.IsRequired ? `${field.label}*` : field.label}
              hint={field.hint}
              name={field.id}
              id={field.id}
              getOptionLabel={(option) => option?.title || option?.country || ""}
              menu={companyCountryOptions}
              value={selectedOption || fallbackOption || null}
              handleChange={(e) => {
                handleChange(field.id, e.target.value || "");
              }}
              valueStyle={{
                backgroundColor: Colors.WHITE,
                height: "50px",
                verticalMarginTop: "4px",
                menuWidth: 400,
                inputWidth: 250,
                padding: "1px",
              }}
              labelStyle={{
                fontSize: "14px",
                color: Colors.BLACK,
              }}
              error={Boolean(errors[field.id])}
              helperText={errors[field.id]}
            />
          );
        }
        return (
          <ConfigDropdown
            label={field.IsRequired ? `${field.label}*` : field.label}
            name={field.id}
            id={field.id}
            menu={field.options}
            value={fieldValue}
            error={errors[field.id]}
            helperText={errors[field.id]}
            handleChange={(_e, value) => handleChange(field.id, value)}
          />
        );

      case BackendFrontend.IMAGES:
        return (
          <Box component="div" sx={{ width: "100%" }}>
            <ImageUpload
              label={field.IsRequired ? `${field.label}*` : field.label}
              name={field.id}
              onImageUpload={(imageValues) => {
                handleImageChange(field.id, imageValues);
              }}
              onImageCancel={(updatedImageUrl) => {
                handleImageCancel(field.id, updatedImageUrl);
              }}
              type="profile"
              formats={pattern.IMAGE_TYPES}
              acceptType="image/*, application/pdf"
              maxImageCount={
                field.id === "primaryPassportDoc" || field.id === "secondaryPassportDoc" ? 2 : 1
              }
              error={Boolean(errors[field.id])}
              helperText={errors[field.id]}
              data={formatFieldValueToArray(fieldValue)}
              showDateFields={
                field.id === "seamansBook" ||
                field.id === "primaryPassportDoc" ||
                field.id === "secondaryPassportDoc" ||
                field.id === "liabilityInsurance" ||
                field.id === "healthInsurance" ||
                field.id === "drivingLicenseDoc" ||
                field.id === "companyRegistrationDoc" ||
                field.id === "bankAccountDoc"
              }
              isDocument={
                field.id === "seamansBook" ||
                field.id.includes("PassportDoc") ||
                field.id === "drivingLicenseDoc" ||
                field.id === "companyRegistrationDoc" ||
                field.id === "bankAccountDoc"
              }
            />
          </Box>
        );

      case BackendFrontend.TAX_ID_NUMBERS: {
        const hasTaxRegistrations = fieldValue && fieldValue.length > 0;
        const dropdownError = errors[field.id] && !hasTaxRegistrations ? errors[field.id] : null;
        return (
          <Box component="div" sx={{ width: "100%" }}>
            <TaxRegistrationField
              initialValues={fieldValue || []}
              isRequired={field.IsRequired}
              onChange={(value) => handleChange(field.id, value)}
              onRegistrationChange={(value) => handleChange(field.id, value)}
              error={dropdownError}
              helperText={dropdownError}
            />
          </Box>
        );
      }

      case BackendFrontend.DATE: {
        // Helper function to get date constraints
        const getDateConstraints = (fieldId) => {
          const constraints = {};

          // Birth date cannot be in the future
          if (fieldId === "birthDate") {
            constraints.max = moment().format(defaultData.DATABSE_DATE_FORMAT);
          }

          // Issue dates max should be set to corresponding expiry dates
          if (fieldId === "primaryPassportIssueDate" && formData.primaryPassportExpiryDate) {
            constraints.max = formData.primaryPassportExpiryDate;
          }

          if (fieldId === "secondaryPassportIssueDate" && formData.secondaryPassportExpiryDate) {
            constraints.max = formData.secondaryPassportExpiryDate;
          }

          if (fieldId === "seamansBookIssueDate" && formData.seamansBookExpiryDate) {
            constraints.max = formData.seamansBookExpiryDate;
          }

          if (fieldId === "drivingLicenseIssueDate" && formData.drivingLicenseExpiryDate) {
            constraints.max = formData.drivingLicenseExpiryDate;
          }

          if (fieldId === "liabilityIssueDate" && formData.liabilityExpiryDate) {
            constraints.max = formData.liabilityExpiryDate;
          }

          if (fieldId === "healthIssueDate" && formData.healthExpiryDate) {
            constraints.max = formData.healthExpiryDate;
          }

          // Expiry dates min should be set to corresponding issue dates
          if (fieldId === "primaryPassportExpiryDate" && formData.primaryPassportIssueDate) {
            constraints.min = formData.primaryPassportIssueDate;
          }

          if (fieldId === "secondaryPassportExpiryDate" && formData.secondaryPassportIssueDate) {
            constraints.min = formData.secondaryPassportIssueDate;
          }

          if (fieldId === "seamansBookExpiryDate" && formData.seamansBookIssueDate) {
            constraints.min = formData.seamansBookIssueDate;
          }

          if (fieldId === "drivingLicenseExpiryDate" && formData.drivingLicenseIssueDate) {
            constraints.min = formData.drivingLicenseIssueDate;
          }

          if (fieldId === "liabilityExpiryDate" && formData.liabilityIssueDate) {
            constraints.min = formData.liabilityIssueDate;
          }

          if (fieldId === "healthExpiryDate" && formData.healthIssueDate) {
            constraints.min = formData.healthIssueDate;
          }

          return constraints;
        };

        const dateConstraints = getDateConstraints(field.id);

        return (
          <FTextField
            label={field.IsRequired ? `${field.label}*` : field.label}
            placeholder={field.hint}
            name={field.id}
            id={field.id}
            type="date"
            value={fieldValue ? moment(fieldValue).format(defaultData.DATABSE_DATE_FORMAT) : ""}
            error={Boolean(errors[field.id])}
            helperText={errors[field.id]}
            handleChange={(e) => handleChange(field.id, e.target.value)}
            InputProps={{
              inputProps: {
                ...dateConstraints,
                style: { textTransform: "uppercase" },
              },
            }}
          />
        );
      }

      default:
        return (
          <FTextField
            label={field.IsRequired ? `${field.label}*` : field.label}
            placeholder={field.hint}
            name={field.id}
            id={field.id}
            type="text"
            value={fieldValue}
            error={Boolean(errors[field.id])}
            helperText={errors[field.id]}
            handleChange={(e) => handleChange(field.id, e.target.value)}
          />
        );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      PaperProps={{
        sx: { width: "90%" },
      }}
    >
      <MDBox
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <MDBox
          px={3}
          py={2}
          display="flex"
          justifyContent="start"
          alignItems="center"
          borderBottom="1px solid #E0E6F5"
        >
          <IconButton onClick={handleDrawerClose} size="small">
            {Icons.CLOSE}
          </IconButton>
          <MDTypography variant="h5" marginLeft={2}>
            Edit Contractual Details
          </MDTypography>
        </MDBox>

        {/* Form */}
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={{
            flexGrow: 1,
            overflow: "auto",
            p: 3,
          }}
        >
          <Grid container spacing={2}>
            {contractualDetailsFields.map((field) => {
              // Check if the field has a condition and if it does not match, skip rendering
              if (field.condition && !field.condition(formData)) {
                return null;
              }

              return (
                <React.Fragment key={field.id}>
                  <Grid item {...field.gridProps}>
                    {renderField(field)}
                  </Grid>
                  {/* Render tax registration fields right after Select Tax Type field */}
                  {field.id === "taxIdNumbers" &&
                    formData.taxIdNumbers &&
                    formData.taxIdNumbers.map((taxReg) => (
                      <Grid item xs={12} key={taxReg.id}>
                        <Box sx={{ position: "relative" }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updated = formData.taxIdNumbers.filter(
                                (r) => r.id !== taxReg.id
                              );
                              handleChange("taxIdNumbers", updated);
                              const newErrors = { ...errors };
                              delete newErrors[`tax-${taxReg.id}-value`];
                              delete newErrors[`tax-${taxReg.id}-documents`];
                              setErrors(newErrors);
                            }}
                            sx={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              zIndex: 1,
                              color: "#FF2E2E",
                              "&:hover": { backgroundColor: "rgba(255, 46, 46, 0.06)" },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: "18px" }} />
                          </IconButton>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={6}>
                              <FTextField
                                label={`${taxReg.type} Number`}
                                name={`tax-${taxReg.id}-value`}
                                id={`tax-${taxReg.id}-value`}
                                type="text"
                                value={taxReg.value || ""}
                                error={Boolean(errors[`tax-${taxReg.id}-value`])}
                                helperText={errors[`tax-${taxReg.id}-value`]}
                                handleChange={(e) => {
                                  const updated = formData.taxIdNumbers.map((r) =>
                                    r.id === taxReg.id
                                      ? { ...r, value: e.target.value.toUpperCase() }
                                      : r
                                  );
                                  handleChange("taxIdNumbers", updated);
                                  if (errors[`tax-${taxReg.id}-value`]) {
                                    const newErrors = { ...errors };
                                    delete newErrors[`tax-${taxReg.id}-value`];
                                    setErrors(newErrors);
                                  }
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <ImageUpload
                                label={`${taxReg.type} Document`}
                                name={`tax-${taxReg.id}-documents`}
                                onImageUpload={(imageValues) => {
                                  const imageInfoArray = imageValues.map((img) => ({
                                    name: img.name || "",
                                    url: img.url || "",
                                    size: img.size || 0,
                                    status: defaultData.PENDING_STATUS,
                                  }));
                                  const updated = formData.taxIdNumbers.map((r) =>
                                    r.id === taxReg.id ? { ...r, documents: imageInfoArray } : r
                                  );
                                  handleChange("taxIdNumbers", updated);
                                  if (errors[`tax-${taxReg.id}-documents`]) {
                                    const newErrors = { ...errors };
                                    delete newErrors[`tax-${taxReg.id}-documents`];
                                    setErrors(newErrors);
                                  }
                                }}
                                onImageCancel={(updatedImageUrl) => {
                                  const filteredImageUrlArray = (updatedImageUrl || []).filter(
                                    (img) => img.url !== Common?.FRAME
                                  );
                                  const updated = formData.taxIdNumbers.map((r) =>
                                    r.id === taxReg.id
                                      ? { ...r, documents: filteredImageUrlArray }
                                      : r
                                  );
                                  handleChange("taxIdNumbers", updated);
                                }}
                                type="profile"
                                formats={pattern.IMAGE_TYPES}
                                acceptType="image/*, application/pdf"
                                maxImageCount={1}
                                error={Boolean(errors[`tax-${taxReg.id}-documents`])}
                                helperText={errors[`tax-${taxReg.id}-documents`]}
                                data={taxReg.documents || []}
                                isDocument
                                showDateFields={false}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    ))}
                </React.Fragment>
              );
            })}
          </Grid>
        </Box>

        {/* Footer */}
        <MDBox
          px={3}
          py={2}
          display="flex"
          justifyContent="flex-end"
          gap={2}
          borderTop="1px solid #E0E6F5"
        >
          <MDButton variant="outlined" color="secondary" onClick={handleDrawerClose}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            style={{ boxShadow: "none", textTransform: "none" }}
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? ButtonTitles.UPDATE_LOADING : "Save Changes"}
          </MDButton>
        </MDBox>
      </MDBox>
    </Drawer>
  );
}

ContractualDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    passport: PropTypes.string,
    identityProof: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        size: PropTypes.number,
        fromDate: PropTypes.string,
        toDate: PropTypes.string,
        isPrimary: PropTypes.bool,
        isSecondary: PropTypes.bool,
      })
    ),
    nationalIdentificationNumber: PropTypes.string,
    drivingLicence: PropTypes.arrayOf(
      PropTypes.shape({
        licenseNumber: PropTypes.string,
        issueDate: PropTypes.string,
        expiryDate: PropTypes.string,
      })
    ),
    seamansBook: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        size: PropTypes.number,
        fromDate: PropTypes.string,
        toDate: PropTypes.string,
      })
    ),
    birthDate: PropTypes.string,
    birthPlace: PropTypes.string,
    employmentType: PropTypes.string,
    liabilityInsurance: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        size: PropTypes.number,
        fromDate: PropTypes.string,
        toDate: PropTypes.string,
      })
    ),
    healthInsurance: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        size: PropTypes.number,
        fromDate: PropTypes.string,
        toDate: PropTypes.string,
      })
    ),
    companyName: PropTypes.string,
    companyRegistrationNumber: PropTypes.string,
    companyAddress: PropTypes.string,
    bankName: PropTypes.string,
    accountHolderName: PropTypes.string,
    bankAccount: PropTypes.string,
    bicSwift: PropTypes.string,
  }),
  contractualDetailId: PropTypes.string,
  userId: PropTypes.string,
  userName: PropTypes.string,
};

ContractualDetailsDrawer.defaultProps = {
  data: {},
  contractualDetailId: "",
  userId: "",
  userName: "",
};

export default ContractualDetailsDrawer;
